import BlockBase from '@components/dnd/base/blockBase';
import { ColumnComp } from '@components/trace/column/column.comp';
import { useAtom } from '@hooks/useAtom';
import EndControlsFB, {
  type SettingsConfig
} from '@library/formBuilder/endControls';
import {
  type ColumnData,
  type DNDBlock,
  ResolvedVariableType
} from '@stratumn/studio-shared';
import { TextWidget } from '@widgets/text/text.widget';
import { observer } from 'mobx-react';
import { useParams } from 'react-router-dom';
import { ParamsList } from '@/routes/routes.types';

import { initialTextData } from './text.data';
import { TextSettingsModal } from './text.settings';
import { TextIcon } from './text.utils';

const TextTableImporterBlock = (block: DNDBlock) => {
  const actionId = useParams()[ParamsList.ActionId] as string;

  const atom = useAtom({
    type: 'Table_Importer_Text',
    sourceId: block.id,
    parentId: actionId,
    parentKind: 'table_importer',
    initialData: initialTextData,
    variableInfo: {
      resolvedType: ResolvedVariableType.Text
    }
  });

  if (!atom) {
    return null;
  }

  const getModalSettings = (): SettingsConfig => ({
    title: atom.data.title || 'Text Column title',
    content: <TextSettingsModal atom={atom} />
  });

  const getEndControls = () => (
    <EndControlsFB
      dataItem={atom}
      dndBlock={block}
      hasRequired
      settings={getModalSettings()}
    />
  );

  const onTitleChange = (title: ColumnData['title']) => {
    if (atom.data.title === atom.data.importerOptions.from) {
      atom.data.importerOptions.from = title; // From follows the column header
    }
    atom.data.title = title;
  };

  return (
    <BlockBase
      dataItem={atom}
      dndBlock={block}
      endControls={getEndControls()}
      icon={TextIcon}
      title={atom.data.title}
    >
      <ColumnComp
        atomId={atom.id}
        isDisplayedByDefault={atom.data.isDisplayedByDefault}
        isfixedColumn={atom.data.isfixedColumn}
        onDisplayByDefaultChange={v => (atom.data.isDisplayedByDefault = v)}
        onFixedColumnChange={v => (atom.data.isfixedColumn = v)}
        onTitleChange={onTitleChange}
        onWidthChange={v => (atom.data.width = v)}
        title={atom.data.title}
        width={atom.data.width}
      />
      <TextWidget
        atomId={atom.id}
        default={atom.data.cell.view.default}
        isRichText={atom.data.cell.view.isRichText}
        onDefaultChange={v => (atom.data.cell.view.default = v)}
        onIsRichTextChange={v => (atom.data.cell.view.isRichText = v)}
      />
    </BlockBase>
  );
};

export default observer(TextTableImporterBlock);
